import React, { useEffect, useState } from 'react';
import { InputText } from '@jobber/components/InputText';
import { InputEmail } from '@jobber/components/InputEmail';
import { InputPassword } from '@jobber/components/InputPassword';
import { showToast } from '@jobber/components/Toast';
import { Button } from '@jobber/components/Button';
import { apiGet, apiPost } from '../../api-client';
import { useNavigate } from 'react-router';
import { Heading } from '@jobber/components/Heading';
import { Link, useLocation } from 'react-router-dom';
import { isValidEmail, validatePassword } from '../../components/Common/Validations';
import { Spinner } from '@jobber/components/Spinner';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { setLoginUser } from '../../store/userSlice';
import Footer from './Components/Footer';
import { hasJobberAccess } from '../../utils/auth';

function Signup() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const location = useLocation();
  const invitationToken = new URLSearchParams(location.search).get('invitationToken');
  const [loading, setLoading] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(Boolean(invitationToken));
  const [invitation, setInvitation] = useState(null);
  const [inviteConfirmationVisible, setInviteConfirmationVisible] = useState(false);
  const [inviteActionLoading, setInviteActionLoading] = useState(false);
  const [formValues, setFormValues] = useState({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: ''
  });
  const setValuesHandler = (name, value) => {
    setFormValues({ ...formValues, [name]: value });
  };
  const validateConfirmPassword = (confirmPasswordValue) => {
    return confirmPasswordValue === formValues.password || 'Password do not match';
  };
  const validatePasswordChar = (passwordValue) => {
    return (
      validatePassword(passwordValue) ||
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
    );
  };

  useEffect(() => {
    const loadInvitation = async () => {
      if (!invitationToken) {
        return;
      }

      try {
        setInviteLoading(true);
        const result = await apiGet(`user/invitation/${invitationToken}`);
        if (result?.status) {
          setInvitation(result.data);
        } else {
          showToast({ message: result?.message || 'Invitation not found', variation: 'error' });
        }
      } catch (error) {
        showToast({ message: error.message || 'Invitation not found', variation: 'error' });
      } finally {
        setInviteLoading(false);
      }
    };

    loadInvitation();
  }, [invitationToken]);

  const finishLogin = async (user) => {
    queryClient.setQueryData(['session'], { status: true, data: { data: user } });

    const [tableSettings, quoteSettings, jobSettings, visitSettings, approvedQuoteSettings] = await Promise.all([
      apiGet(`table-settings/${user._id}`),
      apiGet(`page-settings/${user._id}`),
      apiGet(`job-table-settings/${user._id}`),
      apiGet(`visit-table-Settings/${user._id}`),
      apiGet(`approved-quote/${user._id}`)
    ]);

    if (tableSettings.status && quoteSettings.status && jobSettings.status && visitSettings.status) {
      localStorage.setItem('userSettings', JSON.stringify(tableSettings.data));
      localStorage.setItem('userQuotesSettings', JSON.stringify(quoteSettings.data));
      localStorage.setItem('approvedQuotesSettings', JSON.stringify(approvedQuoteSettings.data));
      localStorage.setItem('jobSettings', JSON.stringify(jobSettings.data));
      localStorage.setItem('visitSettings', JSON.stringify(visitSettings.data));
      dispatch(setLoginUser({ data: user }));
      localStorage.removeItem('cTab');
      document.body.classList.add('body-pd');
      navigate('/clients');
      return true;
    }

    showToast({
      message: tableSettings.message || tableSettings.error || 'Failed to load user settings',
      variation: 'error'
    });
    return false;
  };

  const completeLogin = async () => {
    const result = await apiPost('user/login', {
      email: formValues.email,
      password: formValues.password,
      rememberMe: false,
    });

    if (!result?.status) {
      showToast({
        message: result?.message || result?.error || 'Login failed',
        variation: 'error'
      });
      return false;
    }

    return finishLogin(result.data?.data);
  };

  const acceptInviteHandler = async () => {
    try {
      setInviteActionLoading(true);
      const ok = await completeLogin();
      if (ok) {
        showToast({ message: 'Invitation accepted and logged in successfully', variation: 'success' });
      }
    } catch (error) {
      showToast({
        message: error.message || 'Failed to accept invitation',
        variation: 'error'
      });
    } finally {
      setInviteActionLoading(false);
    }
  };

  const skipInviteHandler = () => {
    navigate('/login');
  };

  const submitInvitationHandler = async (e) => {
    e.preventDefault();

    if (!formValues.password) {
      showToast({ message: 'Password is required', variation: 'error' });
      return;
    }

    if (!validatePassword(formValues.password)) {
      showToast({
        message: 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character',
        variation: 'error'
      });
      return;
    }

    try {
      setLoading(true);
      const result = await apiPost('user/accept-invitation', {
        token: invitationToken,
        password: formValues.password,
      });

      if (result?.status) {
        const ok = await finishLogin(result.data?.data);
        if (ok) {
          showToast({ message: 'Invitation accepted and logged in successfully', variation: 'success' });
        }
      } else {
        showToast({ message: result?.message || result?.error || 'Failed to accept invitation', variation: 'error' });
      }
    } catch (error) {
      showToast({ message: error.message || 'Failed to accept invitation', variation: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault()
    setLoading(true);
    if (!formValues.firstName || !formValues.email || !formValues.password) {
      showToast({ message: 'Please fill out all fields', variation: 'error' });
    } else if (!isValidEmail(formValues.email)) {
      showToast({ message: 'Email is not valid', variation: 'error' });
    } else if (formValues.password != formValues.confirmPassword) {
      showToast({ message: 'Password do not match', variation: 'error' });
    } else if (!validatePassword(formValues.password)) {
      showToast({
        message: 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character',
        variation: 'error'
      });
    } else {
      try {
        const result = await apiPost(`user/signup`, formValues);
        if (result.status) {
          const user = result.data?.data;
          queryClient.setQueryData(['session'], { status: true, data: { data: user } });
          dispatch(setLoginUser({ data: user }));
          localStorage.removeItem('cTab');

          if (invitationToken) {
            showToast({
              message: 'User successfully registered. Please confirm invitation',
              variation: 'success'
            });
            setInviteConfirmationVisible(true);
          } else if (!hasJobberAccess(user)) {
            showToast({
              message: 'User successfully registered. Please connect Jobber to continue',
              variation: 'success'
            });
            navigate('/login?connectJobber=true');
          } else {
            const ok = await finishLogin(user);
            if (ok) {
              showToast({
                message: 'User successfully registered and logged in',
                variation: 'success'
              });
            }
          }
        } else {
          showToast({
            message: result.message || result.error,
            variation: 'error'
          });
        }
      } catch (error) {
        showToast({
          message: error.message || 'Signup failed',
          variation: 'error'
        });
      }
    }
    setLoading(false);
  };
  return (
    <div className="row">
      <div className="text-center mb-4">
        <Heading level={1}>JOBBER X THE ROYAL GATEWAY</Heading>
      </div>
      <div className="col-md-4"></div>
      <div className="col-md-4">
        <div className="d-flex justify-content-between mb-3">
          <Heading level={3}>Signup</Heading>
          <Link to={'/login'}>Login</Link>
        </div>
        <div className="card">
          {invitationToken ? (
            <form className="card-body" onSubmit={submitInvitationHandler}>
              <Heading level={3}>Accept Invitation</Heading>
              {inviteLoading ? (
                <div className="mt-3"><Spinner size="base" /></div>
              ) : (
                <>
                  <div className="border rounded bg-light p-3 mt-3 mb-3">
                    <div className="fw-semibold">{`${invitation?.firstName || ''} ${invitation?.lastName || ''}`.trim() || 'Invited user'}</div>
                    <div className="text-muted small">{invitation?.email}</div>
                    <div className="text-muted small mt-2">{invitation?.organization?.name || 'Organization'} - {invitation?.role}</div>
                  </div>
                  <InputPassword
                    placeholder="Password"
                    name="password"
                    hasVisibility={true}
                    value={formValues.password}
                    onChange={(value) => setValuesHandler('password', value)}
                    validations={{
                      required: { value: true, message: 'Password is required' },
                      validate: validatePasswordChar,
                      minLength: {
                        value: 8,
                        message: 'Password must be at least 8 characters'
                      }
                    }}
                  />
                  <div className="mt-3"></div>
                  {loading ? <Spinner size="base" /> : <Button label={'Accept Invitation'} submit />}
                </>
              )}
            </form>
          ) : inviteConfirmationVisible ? (
            <div className="card-body">
              <Heading level={3}>Accept Invitation</Heading>
              <p className="mt-3 mb-3">Do you want to accept this organization invitation now?</p>
              <p className="mb-3">Accepting will log you in and attach this invitation to your account.</p>
              <div className="d-flex gap-2">
                {inviteActionLoading ? (
                  <Spinner size="base" />
                ) : (
                  <>
                    <Button label={'Accept Invite'} onClick={acceptInviteHandler} />
                    <Button label={'Skip for now'} type="secondary" onClick={skipInviteHandler} />
                  </>
                )}
              </div>
            </div>
          ) : (
            <form className="card-body" onSubmit={submitHandler}>
              <InputText
                placeholder="First Name"
                name="firstName"
                value={formValues.firstName}
                onChange={(value) => setValuesHandler('firstName', value)}
                validations={{
                  required: {
                    value: true,
                    message: 'Tell us your first name'
                  }
                }}
              />
              <div className="mt-3"></div>
              <InputText
                placeholder="Last Name"
                name="lastName"
                value={formValues.lastName}
                onChange={(value) => setValuesHandler('lastName', value)}
                validations={{
                  required: {
                    value: true,
                    message: 'Tell us your last name'
                  }
                }}
              />
              <div className="mt-3"></div>

              <InputEmail
                placeholder="Email"
                name="email"
                value={formValues.email}
                onChange={(value) => setValuesHandler('email', value)}
                validations={{
                  required: {
                    value: true,
                    message: 'Tell us your email'
                  }
                }}
              />
              <div className="mt-3"></div>

              <InputPassword
                placeholder="Password"
                name="password"
                hasVisibility={true}
                value={formValues.password}
                onChange={(value) => setValuesHandler('password', value)}
                validations={{
                  required: { value: true, message: 'Password is required' },
                  validate: validatePasswordChar,
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters'
                  }
                }}
              />
              <div className="mt-3"></div>

              <InputPassword
                placeholder="Confirm Password"
                name="confirmpassword"
                hasVisibility={true}
                value={formValues.confirmPassword}
                onChange={(value) => setValuesHandler('confirmPassword', value)}
                validations={{
                  required: { value: true, message: 'Password is required' },
                  validate: validateConfirmPassword,
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters'
                  }
                }}
              />
              <div className="mt-3"></div>
              {loading ? <Spinner size="base" /> : <Button label={'Sign Up'} submit />}
            </form>
          )}
        </div>
      </div>
      <div className="col-md-4"></div>
      <Footer />
    </div>
  );
}

export default Signup;
